import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SocialShare from '@/components/feature/SocialShare';
import { getArticleBySlug, getRelatedArticles, articles } from './articles';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const ArticleJsonLd = ({ article }: { article: ReturnType<typeof getArticleBySlug> }) => {
  if (!article) return null;
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  };
  const blogPostSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "url": `${SITE_URL}/blog/${article.slug}`,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Organization",
      "name": "Greenly Electronics Recycling",
      "url": SITE_URL,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Greenly Electronics Recycling",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`,
      },
    },
    "image": article.image,
    "description": article.excerpt,
    "articleSection": article.category,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${article.slug}`,
    },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
      { "@type": "ListItem", "position": 2, "name": "Blog & Resources", "item": `${SITE_URL}/blog` },
      { "@type": "ListItem", "position": 3, "name": article.title, "item": `${SITE_URL}/blog/${article.slug}` },
    ],
  };
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(blogPostSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </>
  );
};

const ArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug ?? '');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  if (!article) {
    return (
      <>
        <title>Article Not Found | Greenly Electronics Recycling</title>
        <Navbar />
        <main className="pt-28 pb-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#E8F5EE] rounded-full mx-auto mb-4">
              <i className="ri-article-line text-[#1B6B3A] text-2xl"></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Article Not Found</h1>
            <p className="text-gray-500 mb-6">
              The article you are looking for does not exist or has been moved.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.relatedSlugs);
  const otherArticles = articles
    .filter((a) => a.slug !== article.slug && !article.relatedSlugs.includes(a.slug))
    .slice(0, Math.max(0, 3 - relatedArticles.length));
  const allRelated = [...relatedArticles, ...otherArticles].slice(0, 3);

  return (
    <>
      <title>{article.metaTitle}</title>
      <meta name="description" content={article.metaDescription} />
      <meta name="keywords" content={article.metaKeywords} />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content={article.date} />
      <link rel="canonical" href={`${SITE_URL}/blog/${article.slug}`} />

      {/* Open Graph / Social Share Meta Tags */}
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.metaDescription} />
      <meta property="og:image" content={article.image} />
      <meta property="og:url" content={`${SITE_URL}/blog/${article.slug}`} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Greenly Electronics Recycling" />
      <meta property="og:locale" content="en_US" />
      <meta property="article:published_time" content={article.date} />
      <meta property="article:section" content={article.category} />
      <meta property="article:tag" content={article.metaKeywords.split(',').slice(0, 5).join(',')} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.metaDescription} />
      <meta name="twitter:image" content={article.image} />
      <meta name="twitter:site" content="@greenlyrecycling" />
      <meta name="twitter:creator" content="@greenlyrecycling" />

      <ArticleJsonLd article={article} />
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="bg-[#0F3D22] pt-28 pb-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: 'Blog & Resources', path: '/blog' },
                { label: article.title },
              ]}
            />
            <div className="flex items-center gap-3 mt-5 mb-4">
              <span className="text-xs font-semibold text-amber-300 bg-amber-900/30 px-2.5 py-1 rounded-full">
                {article.category}
              </span>
              <span className="text-xs text-green-300">{article.date}</span>
              <span className="text-xs text-green-300">{article.readTime}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-5">
              {article.title}
            </h1>
            <div className="mt-4">
              <SocialShare url={`${SITE_URL}/blog/${article.slug}`} title={article.title} />
            </div>
          </div>
        </section>

        {/* Hero Image */}
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-6">
            <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover object-top"
                loading="eager"
                width={900}
                height={500}
              />
            </div>
          </div>
        </section>

        {/* Article Body */}
        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            {/* Excerpt */}
            <p className="text-lg text-gray-600 leading-relaxed mb-10 font-medium">
              {article.excerpt}
            </p>

            {/* Content Sections */}
            <article className="space-y-10">
              {article.content.map((section, idx) => (
                <section key={idx}>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p key={pIdx} className="text-gray-600 leading-relaxed mb-4 text-sm md:text-base">
                      {para}
                    </p>
                  ))}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="space-y-3 mt-4">
                      {section.bullets.map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0 mt-0.5">
                            <i className="ri-check-line text-[#1B6B3A] text-xs"></i>
                          </div>
                          <span className="text-gray-600 text-sm leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </article>

            {/* Internal Links */}
            <div className="mt-10 pt-6 border-t border-gray-100">
              <SocialShare url={`${SITE_URL}/blog/${article.slug}`} title={article.title} />
            </div>

            <div className="mt-8 p-5 bg-[#F4FAF7] rounded-xl border border-green-100">
              <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wider">
                Related Services
              </h3>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'TV Recycling', path: '/tv-recycling' },
                  { label: 'Computer Recycling', path: '/computer-recycling' },
                  { label: 'Battery Recycling', path: '/battery-recycling' },
                  { label: 'Hard Drive Destruction', path: '/hard-drive-destruction' },
                  { label: 'EV Charger Recycling', path: '/ev-charging-recycling' },
                  { label: 'Free Drop-Off', path: '/dropoff-form' },
                  { label: 'Schedule Pickup', path: '/pickup-request' },
                ].map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-sm text-[#1B6B3A] font-medium hover:underline cursor-pointer whitespace-nowrap"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">
                Common Questions
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-3">
              {article.faqs.map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-extrabold text-gray-900 text-sm">{faq.q}</h4>
                    <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0">
                      <i
                        className={`ri-arrow-down-s-line text-[#1B6B3A] transition-transform duration-200 ${
                          openFaq === i ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {allRelated.length > 0 && (
          <section className="py-14 bg-white">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {allRelated.map((ra) => (
                  <article
                    key={ra.slug}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#1B6B3A]/30 transition-all group"
                  >
                    <Link to={`/blog/${ra.slug}`} className="block">
                      <div className="w-full h-44 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          width={400}
                          height={176}
                        />
                      </div>
                    </Link>
                    <div className="p-4">
                      <span className="text-xs font-semibold text-[#1B6B3A] bg-[#E8F5EE] px-2 py-0.5 rounded-full">
                        {ra.category}
                      </span>
                      <Link to={`/blog/${ra.slug}`} className="block mt-2">
                        <h3 className="text-base font-bold text-gray-900 group-hover:text-[#1B6B3A] transition-colors leading-snug">
                          {ra.title}
                        </h3>
                      </Link>
                      <p className="text-gray-500 text-sm mt-2 line-clamp-2">{ra.excerpt}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="bg-[#F4FAF7] py-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Ready to Recycle Your Electronics?
            </h2>
            <p className="text-gray-500 mb-8">
              Free residential drop-off and business pickup throughout Los Angeles County. Certified responsible recycling since 2010.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/dropoff-form"
                className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-2-line"></i>
                Free Drop-Off
              </Link>
              <Link
                to="/pickup-request"
                className="inline-flex items-center gap-2 border-2 border-[#1B6B3A] text-[#1B6B3A] font-semibold px-7 py-3.5 rounded-full hover:bg-[#E8F5EE] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-truck-line"></i>
                Schedule Pickup
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ArticlePage;